import { PersonalizationStatus } from "@/types/types";
import { create } from "zustand";
import { persist } from "zustand/middleware";

type SidebarState = "expanded" | "collapsed" | "hidden";

interface GlobalStore {
  niches: string[];
  initialNiches: string[];
  setInitialNiches: (niches: string[]) => void;
  setNiches: (niches: string[]) => void;
  addNiche: (niche: string) => void;
  removeNiche: (niche: string) => void;
  subscriptionModal: boolean;
  toggleSubscriptionModal: (state: boolean) => void;
  personalizationStatus: PersonalizationStatus;
  updatePersonalizationStatus: (status: PersonalizationStatus) => void;
  sidebarState: SidebarState;
  setSidebarState: (state: SidebarState) => void;
  // New scroll related state
  isGenerating: boolean;
  setIsGenerating: (state: boolean) => void;
  shouldScrollToBottom: boolean;
  setShouldScrollToBottom: (state: boolean) => void;
  contentChanged: boolean;
  setContentChanged: (state: boolean) => void;
}

const useGlobalStore = create<GlobalStore>()(
  persist(
    (set) => ({
      niches: [],
      initialNiches: [],
      setInitialNiches: (niches) => {
        set({ initialNiches: niches });
      },
      setNiches: (niches) => set({ niches }),
      addNiche: (niche) =>
        set((state) =>
          state.niches.includes(niche)
            ? state
            : { niches: [...state.niches, niche] }
        ),
      removeNiche: (niche) =>
        set((state) => ({
          niches: state.niches.filter((n) => n !== niche),
        })),
      subscriptionModal: false,
      toggleSubscriptionModal: (val: boolean) => {
        set({
          subscriptionModal: val,
        });
      },
      personalizationStatus: "idle",
      updatePersonalizationStatus(status) {
        set({
          personalizationStatus: status,
        });
      },
      sidebarState: "collapsed",
      setSidebarState(state) {
        set({
          sidebarState: state,
        });
      },
      // New scroll related state implementation
      isGenerating: false,
      setIsGenerating: (state) => set({ isGenerating: state }),
      shouldScrollToBottom: false,
      setShouldScrollToBottom: (state) => set({ shouldScrollToBottom: state }),
      contentChanged: false,
      setContentChanged: (state) => set({ contentChanged: state }),
    }),
    {
      name: "global-storage",
      partialize: (state) => ({
        personalizationStatus: state.personalizationStatus,
      }),
    }
  )
);

export const useSubscriptionModal = () =>
  useGlobalStore((state) => state.subscriptionModal);
export const useGlobalStoreActions = () => ({
  toggleSubscriptionModal: useGlobalStore(
    (state) => state.toggleSubscriptionModal
  ),
  updatePersonalizationStatus: useGlobalStore(
    (state) => state.updatePersonalizationStatus
  ),
  setSidebarState: useGlobalStore((state) => state.setSidebarState),
  // Add new scroll actions
  setIsGenerating: useGlobalStore((state) => state.setIsGenerating),
  setShouldScrollToBottom: useGlobalStore(
    (state) => state.setShouldScrollToBottom
  ),
  setContentChanged: useGlobalStore((state) => state.setContentChanged),
  removeNiche: useGlobalStore((state) => state.removeNiche),
  addNiche: useGlobalStore((state) => state.addNiche),
  setNiches: useGlobalStore((state) => state.setNiches),
  setInitialNiches: useGlobalStore((state) => state.setInitialNiches),
});

export const usePersonalizationStatus = () =>
  useGlobalStore((state) => state.personalizationStatus);
export const useSidebarState = () =>
  useGlobalStore((state) => state.sidebarState);
export const useScrollState = () => ({
  isGenerating: useGlobalStore((state) => state.isGenerating),
  shouldScrollToBottom: useGlobalStore((state) => state.shouldScrollToBottom),
  contentChanged: useGlobalStore((state) => state.contentChanged),
});

export const useNiches = () => useGlobalStore((state) => state.niches);
export const useInitialNiches = () =>
  useGlobalStore((state) => state.initialNiches);
